import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import './Students.css';

const Students = () => {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [totalStudents, setTotalStudents] = useState(0);
  const [activeStudents, setActiveStudents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/users?role=student&limit=200');
      const list = response.data.users || [];
      setStudents(list);
      setTotalStudents(parseInt(response.data.total || list.length, 10));
      setActiveStudents(list.filter((student) => student.is_active).length);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      await api.put(`/admin/users/${id}`, { is_active: !currentStatus });
      setStudents((prev) =>
        prev.map((student) =>
          student.id === id ? { ...student, is_active: !currentStatus } : student
        )
      );
    } catch (error) {
      console.error('Error updating student:', error);
      alert(t('students.statusFailed'));
    }
  };

  const filteredStudents = students.filter((student) => {
    const term = searchTerm.toLowerCase();
    return (
      student.first_name?.toLowerCase().includes(term) ||
      student.last_name?.toLowerCase().includes(term) ||
      student.email?.toLowerCase().includes(term)
    );
  });

  const renderStudentAccordion = () => (
    <div className="course-accordion-container">
      {filteredStudents.length === 0 ? (
        <p className="no-data">{t('students.noStudents')}</p>
      ) : (
        <div className="accordion-list">
          {filteredStudents.map((student) => (
            <div
              key={student.id}
              className={`accordion-item ${expandedId === student.id ? 'expanded' : ''}`}
            >
              <div className="accordion-header" onClick={() => setExpandedId(expandedId === student.id ? null : student.id)}>
                <div className="header-info">
                  <div className="title-group">
                    <span className="course-title">{student.first_name} {student.last_name}</span>
                    <span className="course-category">ID: {student.id}</span>
                  </div>
                  <div className="meta-group">
                    <span className="instructor-badge">{student.email}</span>
                    <span className={`status-pill ${student.is_active ? 'active' : 'inactive'}`}>
                      {student.is_active ? t('students.activeStatus') : t('students.inactiveStatus')}
                    </span>
                    <span className="learner-count">
                      {t('students.joinedLabel')} {new Date(student.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="header-actions">
                  <button
                    type="button"
                    className="btn-icon-toggle"
                    aria-label="Toggle Details"
                  >
                    {expandedId === student.id ? '−' : '+'}
                  </button>
                </div>
              </div>

              {expandedId === student.id && (
                <div className="accordion-content">
                  <div className="content-grid">
                    <div className="description-section">
                      <h5>{t('students.accountDetails')}</h5>
                      <p>{t('students.fullName')}: {student.first_name} {student.last_name}</p>
                      <p>{t('students.emailLabel')}: {student.email}</p>
                      <p>{t('students.phoneLabel')}: {student.phone || t('students.notProvided')}</p>
                    </div>
                    <div className="stats-section">
                      <div className="stat-item">
                        <span className="label">{t('students.joinedLabel')}</span>
                        <strong>{new Date(student.created_at).toLocaleDateString()}</strong>
                      </div>
                      <div className="stat-item">
                        <span className="label">{t('students.lastLogin')}</span>
                        <strong>
                          {student.last_login
                            ? new Date(student.last_login).toLocaleDateString()
                            : '—'}
                        </strong>
                      </div>
                      <div className="stat-item">
                        <span className="label">{t('students.roleLabel')}</span>
                        <strong>{t('students.studentRole')}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="content-actions">
                    <button
                      className={student.is_active ? 'btn-warning' : 'btn-success'}
                      onClick={() => handleStatusToggle(student.id, student.is_active)}
                    >
                      {student.is_active ? t('students.deactivate') : t('students.activate')}
                    </button>
                    {/* Add more admin actions if needed, like viewing transcript or resetting password */}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) return <div className="loading">{t('students.loading')}</div>;

  return (
    <div className="page students-page">
      <div className="container">
        <section className="students-hero">
          <div className="hero-content">
            <p className="eyebrow">{t('students.eyebrow')}</p>
            <h1>{t('students.title')}</h1>
            <p>{t('students.subtitle')}</p>
          </div>
          <div className="hero-stats">
            <div className="hero-stat-card">
              <span className="stat-label">{t('students.totalStudents')}</span>
              <p className="stat-value">{totalStudents}</p>
            </div>
            <div className="hero-stat-card">
              <span className="stat-label">{t('students.active')}</span>
              <p className="stat-value">{activeStudents}</p>
            </div>
          </div>
        </section>

        <div className="course-management-section">
          <div className="section-header-row">
            <div className="panel-header-simple">
              <p className="label">{t('students.management')}</p>
              <h2>{t('students.directory')}</h2>
            </div>
            <div className="search-bar-container">
              <input
                type="text"
                className="search-input"
                placeholder={t('students.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          {renderStudentAccordion()}
        </div>
      </div>
    </div>
  );
};

export default Students;
